<template>
    <main>
        <!-- Loader: no se muestra nada hasta resolver Prismic (evita el flash de contenido default). -->
        <div v-if="!pageReady" class="clc-page-loader">
            <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
        </div>
        <template v-else>
        <SideBar
            :current-language="prismicLang"
            :clc-name="clcName"
            :highlight="sidebarHighlight"
            :dynamic-sidebar="dynamicSidebar"
            @toggle-language="toggleLanguage"
        />
        <section class="CLCSite">
            <div class="clc-hero">
                <h1 class="center-wrap" :data-lang="prismicLang" v-html="bannerData.title"></h1>
                <!--
                Quick-links como acordeón por hover. Data: grupo `categories` de clc_new_home_page.
                `subtitle` (azul) = campo nuevo en Prismic; `description` (gris) = campo ya existente.
                -->
                <ul class="grid-quick-links" v-if="bannerData.icons && bannerData.icons.length > 0">
                    <li v-for="(icon, index) in bannerData.icons" :key="index" class="ql-row">
                        <a
                            class="ql-row__link"
                            :href="getCategoryURL(icon)"
                            :target="getCategoryIfTargetBlank(icon)"
                            :title="getCategoryTitle(icon)"
                            :data-label="`${getCategoryTitle(icon)} ${getCategorySubtitle(icon)}`"
                        >
                            <div class="ql-row__head">
                                <span class="ql-row__icon">
                                    <img
                                        v-if="getCategoryImage(icon)"
                                        :src="getCategoryImage(icon)"
                                        :alt="getCategoryImageAlt(icon)"
                                    />
                                </span>
                                <span class="ql-row__titles">
                                    <h2 class="ql-row__title">{{ getCategoryTitle(icon) }}</h2>
                                    <span v-if="getCategorySubtitle(icon)" class="ql-row__subtitle">{{ getCategorySubtitle(icon) }}</span>
                                </span>
                                <span class="ql-row__go">
                                    <span class="ql-row__go-label">{{ translations.visitPage }}</span>
                                    <span class="ql-row__go-icon" aria-hidden="true">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M5 12h14"/>
                                            <path d="M13 6l6 6-6 6"/>
                                        </svg>
                                    </span>
                                </span>
                            </div>
                            <div v-if="getCategoryDescription(icon)" class="ql-row__panel">
                                <div class="ql-row__panel-inner">
                                    <p class="ql-row__desc">{{ getCategoryDescription(icon) }}</p>
                                </div>
                            </div>
                        </a>
                    </li>
                </ul>
                <div class="nav-web" :data-lang="prismicLang">
                    <a href="https://www.google.com/" target="_blank" rel="noopener noreferrer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.0514 9.06045L13.5586 12.5684C13.8315 12.8413 13.8324 13.2828 13.5564 13.5589C13.2823 13.8331 12.8339 13.8291 12.566 13.5612L9.05876 10.0533C6.85588 11.768 3.66946 11.6128 1.64466 9.58759C-0.548221 7.3943 -0.548221 3.83827 1.64466 1.64497C3.83755 -0.548324 7.39291 -0.548324 9.5858 1.64497C11.6106 3.67015 11.7658 6.85717 10.0514 9.06045ZM8.59315 8.59477C10.2378 6.94979 10.2378 4.28277 8.59315 2.6378C6.94849 0.992828 4.28197 0.992828 2.6373 2.6378C0.992642 4.28277 0.992642 6.94979 2.6373 8.59477C4.28197 10.2397 6.94849 10.2397 8.59315 8.59477Z" fill="#5B5B5B"/>
                        </svg>
                        <span>{{ translations.navWebText }}</span>
                    </a>
                </div>
            </div>

            <!-- News highlight -->
            <NewsHome
                :news="newsData || []"
                :prismic-lang="prismicLang"
                :clc-segment-value="clcSegmentValue"
                :featured-cta-label="homePageData && homePageData.highlighted_new_cta_text"
            />

            <!-- Cintillo / Banner -->
            <div v-if="cintilloData.enabled && (cintilloData.imageDesktop || cintilloData.imageMobile)" class="Banner-clc">
                <a
                    v-if="cintilloData.link"
                    :href="cintilloData.link"
                    :alt="cintilloData.title"
                    :target="cintilloData.blank ? '_blank' : '_self'"
                    :data-label="`Minibanner - ${cintilloData.title}`"
                >
                    <picture>
                        <source
                            v-if="cintilloData.imageMobile"
                            media="(max-width: 767px)"
                            :srcset="cintilloData.imageMobile"
                        />
                        <img
                            :src="cintilloData.imageDesktop"
                            :alt="cintilloData.title"
                        />
                    </picture>
                </a>
                <picture v-else>
                    <source
                        v-if="cintilloData.imageMobile"
                        media="(max-width: 767px)"
                        :srcset="cintilloData.imageMobile"
                    />
                    <img
                        :src="cintilloData.imageDesktop"
                        :alt="cintilloData.title"
                    />
                </picture>
            </div>

            <!-- Etapa 4 (ACHIEVERY-1363): Stay in the loop (form email -> CLC, solo código). -->
            <StayInTheLoop
                :prismic-lang="prismicLang"
                :clc-name="clcName"
                :clc-segment-value="clcSegmentValue"
            />

            <!-- Etapa 5 (ACHIEVERY-1363): Help & Support (videos + FAQs). Cierra los módulos antes del footer. -->
            <HelpSupport :prismic-lang="prismicLang" />

            <!-- Badge de reCAPTCHA v3 del form de "Stay in the loop": inline acá, ya no flotando. -->
            <RecaptchaBadge :prismic-lang="prismicLang" />

        </section>

        <footer>
            <p>
                <img src="../assets/images/rebrand-2026/logo-footer.png" alt="ATT Logo" />
                <span>Corporate Responsibility</span>
            </p>
            <ul>
                <li>
                    <a href="https://www.att.com/" data-label="AT&T" target="_blank" rel="noopener" alt="AT&T">AT&T</a>
                </li>
                <li>
                    <a href="https://sustainability.att.com/" :data-label="`${prismicLang === 'en-us' ? 'Sustainability' : 'Sostenibilidad'} ATT`" target="_blank" rel="noopener" :alt="`${prismicLang === 'en-us' ? 'Sustainability' : 'Sostenibilidad'} ATT`">{{ prismicLang === 'en-us' ? 'SUSTAINABILITY' : 'SOSTENIBILIDAD' }} </a>
                </li>
            </ul>
        </footer>
        </template>
    </main>
</template>

<script>
import SideBar from './SideBar.vue';
import Const from '../constants';
import { getCleanPrismicImageUrl } from './helpers/prismicHelpers';
import { mapSidebarData, saveSidebarCache, preloadImage, CLC_HOME_PAGE_TYPE } from './helpers/sidebarHelpers';
import NewsHome from './subcomponents/NewsHome.vue';
import StayInTheLoop from './subcomponents/StayInTheLoop.vue';
import HelpSupport from './subcomponents/HelpSupport.vue';
import RecaptchaBadge from './subcomponents/RecaptchaBadge.vue';

export default {
    name: 'CLCHomePage',
    components: {
        SideBar,
        NewsHome,
        StayInTheLoop,
        HelpSupport,
        RecaptchaBadge
    },
    data() {
        return {
            prismicLang: 'en-us',
            clcName: '',

            // Prismic data
            homePageDocument: null,
            newsDocument: null,
            homePageData: null,
            newsData: null,

            // Processed data from Prismic
            bannerData: {
                title: '',
                description: '',
                icons: [],
            },
            cintilloData: {
                enabled: false,
                title: '',
                description: '',
                link: '',
                blank: false,
                linkText: '',
                imageDesktop: '',
                imageMobile: ''
            },
            // Bloque destacado del rail (SidebarHighlight). Se mapea desde clc_new_home_page.
            sidebarHighlight: { template: 'empty' },
            // Doodle: logo + fondo custom del rail (global, on/off manual desde Prismic).
            dynamicSidebar: { logoSrc: '', bgImage: '' },

            clcSegmentValue: '',
            isLoadingInitialNews: true, // Estado de carga inicial de noticias
            pageReady: false, // Gate: no renderizar contenido hasta resolver Prismic (evita flash default)
        }
    },
    
    computed: {
        newsDataHasItems() {
            return this.newsData?.length > 0;
        },

        translations() {
            const texts = {
                'en-us': {
                    // META
                    pageTitle: 'Connected Learning Center',
                    pageDescription: 'Welcome to your connected learning experience!',

                    switchLanguage: 'Cambiar a Español',
                    logoAlt: 'ATT Connected Learning Center',
                    navWebText: 'Start your Internet search here',
                    visitPage: 'Visit page', // Etapa 2: CTA de quick-links

                    // NEWS SECTION - TODO: These are NEW in Rebrand
                    newsTitle: 'News & Updates',
                    noResults: 'No news found',
                    noResultsMessage: 'Try adjusting or removing the filters to see more content.',
                    noNewsAvailable: 'No news available',
                    noNewsMessage: 'There are no news articles available at the moment. Please check back later.',
                    clearFilters: 'Clear all filters',
                    noMoreNews: "You're all caught up!",
                    learnMore: 'Learn more'
                },
                'es-es': {
                    // META
                    pageTitle: 'Connected Learning Center',
                    pageDescription: '¡Te damos la bienvenida al aprendizaje online!',

                    switchLanguage: 'Switch to English',
                    logoAlt: 'Centro de aprendizaje conectado de ATT',
                    navWebText: 'Comienza tu búsqueda en Internet aquí',
                    visitPage: 'Visitar página', // Etapa 2: CTA de quick-links

                    // NEWS SECTION - TODO: These are NEW in Rebrand
                    newsTitle: 'Noticias y Actualizaciones',
                    noResults: 'No se encontraron noticias',
                    noResultsMessage: 'Intenta ajustar o eliminar los filtros para ver más contenido.',
                    noNewsAvailable: 'No hay noticias disponibles',
                    noNewsMessage: 'No hay noticias disponibles en este momento. Por favor, vuelve más tarde.',
                    clearFilters: 'Limpiar filtros',
                    noMoreNews: 'Estás al día; ya has visto todas las noticias.',
                    learnMore: 'Leer más'
                }
            };
            return texts[this.prismicLang] || texts['en-us'];
        }
    },

    methods: {
        setLanguage() {
            if (this.$route.path.startsWith('/es') || this.$route.path.startsWith('/en')) {
                this.prismicLang = this.$route.path.startsWith('/es') ? 'es-es' : 'en-us';
                return;
            }

            const browserLang = navigator.language || navigator.userLanguage; 
            let path = this.$route.path;
            let query = this.$route.query;

            const preferredLang = browserLang.startsWith('es') ? 'es' : 'en';
            const newPath = `/${preferredLang}${path}`;

            if (newPath !== path) {
                this.$router.replace({ path: newPath, query });
            }

            this.prismicLang = preferredLang === 'es' ? 'es-es' : 'en-us';
        },

        updateMeta() {
            const title = this.translations.pageTitle;
            const content = this.translations.pageDescription;
            
            const meta = document.querySelector('meta[name="description"]');
            if (meta) {
                meta.setAttribute('content', content);
            } else {
                const metaTag = document.createElement('meta');
                metaTag.setAttribute('name', 'description');
                metaTag.setAttribute('content', content);
                document.head.appendChild(metaTag);
            }

            document.title = title;
        },

        async toggleLanguage() {
            this.sendSwitchLanguage();

            const currentQuery = this.$route.query;
            const currentPath = this.$route.path;
            let newPath = currentPath;

            if (this.prismicLang === 'en-us') {
                newPath = currentPath.replace(/^\/en/, '/es');
                this.prismicLang = 'es-es';
            } else {
                newPath = currentPath.replace(/^\/es/, '/en');
                this.prismicLang = 'en-us';
            }

            if (newPath !== currentPath) {
                this.$router.push({ path: newPath, query: currentQuery });
            }

            this.updateMeta();
            await this.retrieveData();
            this.sendPageView();
        },

        // SEO and Analytics Methods
        sendPageView() {
            console.log("sendPageView");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'pageview',
                'language': this.prismicLang || 'unknown'
            });
        },

        sendSwitchLanguage() {
            console.log("sendSwitchLanguage");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'switch_language',
                'button_text': this.prismicLang === 'es-es' ? "Switch to English" : "Cambiar a Español"
            });
        },

        // PRISMIC DATA METHODS
        getCenterParam() {
            const urlParams = new URLSearchParams(window.location.search);
            this.clcSegmentValue = urlParams.get(Const.CENTER) ?? this.clcSegmentValue;
        },

        async getHomePageData() {
            try {
                const options = {
                    lang: this.prismicLang,
                };

                const response = await this.$prismic.client.getSingle(CLC_HOME_PAGE_TYPE, options);
                this.homePageDocument = response;
                this.homePageData = response?.data;
            } catch (error) {
                console.error("METHOD: getHomePageData ==> ", error);
            }
        },

        getBannerData() {
            const data = this.homePageData;
            this.bannerData = {
                title: data?.title?.[0]?.text || this.bannerData.title,
                description: data?.description?.[0]?.text || this.bannerData.description,
                // Grupo `categories` de clc_new_home_page. Cada item: image_title, subtitle (azul),
                // description (gris, acordeón), image, link.
                icons: data?.categories || []
            };
        },

        getCintilloData() {
            this.cintilloData = {
                enabled: false,
                title: '',
                description: '',
                link: '',
                blank: false,
                linkText: '',
                imageDesktop: '',
                imageMobile: ''
            };

            const data = this.homePageData;

            // Primero buscar cintillo local específico para este segment
            const lookUpLocal = data?.cintillos_locales?.find(cintillo =>
                cintillo.segment_cintillo_local === this.clcSegmentValue &&
                cintillo.cintillo_local_on_off === true &&
                cintillo.titulo_cintillo_local?.[0]?.text
            );

            if (lookUpLocal) {
                this.cintilloData = {
                    enabled: true,
                    title: lookUpLocal?.titulo_cintillo_local?.[0]?.text || '',
                    description: lookUpLocal?.body_text?.[0]?.text || '',
                    link: lookUpLocal?.link?.url || '',
                    linkText: lookUpLocal?.link_text?.[0]?.text || '',
                    blank: lookUpLocal?.link?.target === '_blank',
                    imageDesktop: getCleanPrismicImageUrl(lookUpLocal?.image_local?.url),
                    imageMobile: getCleanPrismicImageUrl(lookUpLocal?.image_local?.mobile?.url)
                };
            } else if (data?.cintillo_general_on_off && data?.titulo_cintillo_general?.[0]?.text) {
                // Si no hay cintillo local, usar el cintillo general
                this.cintilloData = {
                    enabled: true,
                    title: data.titulo_cintillo_general?.[0]?.text || '',
                    description: data?.default_body_text?.[0]?.text || '',
                    link: data?.default_link?.url || '',
                    linkText: data?.default_link_text?.[0]?.text || '',
                    imageDesktop: getCleanPrismicImageUrl(data?.image_general?.url),
                    blank: data?.default_link?.target === '_blank',
                    imageMobile: getCleanPrismicImageUrl(data?.image_general?.mobile?.url)
                };
            }
        },

        // Mapea el bloque destacado del rail (SidebarHighlight) y el doodle (logo/fondo)
        // desde clc_new_home_page. Ambos globales; el mapeo se comparte con las páginas de noticias.
        getSidebarData() {
            const { highlight, dynamicSidebar } = mapSidebarData(this.homePageData);
            this.sidebarHighlight = highlight;
            this.dynamicSidebar = dynamicSidebar;
            // Cachear el branding global para hidratar el rail en las páginas de noticias (sin flash).
            saveSidebarCache(this.prismicLang, { highlight, dynamicSidebar });
        },

        // Category helper methods (same as CLCHomePage.vue)
        getCategoryTitle(icon) {
            return icon?.image_title?.[0]?.text || '';
        },

        getCategoryURL(icon) {
            return icon?.link?.url || '';
        },

        getCategoryImage(icon) {
            return getCleanPrismicImageUrl(icon?.image?.url);
        },

        getCategoryImageAlt(icon) {
            return icon?.image?.alt || '';
        },

        getCategoryIfTargetBlank(icon) {
            return icon?.link?.target ?? '';
        },

        getCategoryDescription(icon) {
            return icon?.description?.[0]?.text || '';
        },

        // Subtítulo azul corto del quick-link (campo `subtitle` del grupo categories).
        getCategorySubtitle(icon) {
            return icon?.subtitle?.[0]?.text || '';
        },

        async getNewsData() {
            try {
                this.isLoadingInitialNews = true;

                // Noticias curadas desde clc_new_home_page > grupo `news` (repetible), cada item con
                // un Content Relationship `news_content` a clc_news. El ORDEN manda: la 1ª se muestra
                // grande. Hasta 6. Selección global (igual para todos los CLCs). Sin picks -> módulo oculto.
                const ids = (this.homePageData?.news || [])
                    .map(item => item?.news_content)
                    .filter(link => link && link.id && link.link_type === 'Document' && !link.isBroken)
                    .map(link => link.id)
                    .slice(0, 6);

                if (!ids.length) {
                    this.newsData = [];
                    return;
                }

                const response = await this.$prismic.client.getByIDs(ids, { lang: this.prismicLang });
                const results = response?.results || [];
                // getByIDs no garantiza el orden pedido -> reordenar según el picker.
                const byId = {};
                results.forEach(doc => { byId[doc.id] = doc; });
                this.newsDocument = response;
                this.newsData = ids.map(id => byId[id]).filter(Boolean);
            } catch (error) {
                console.error("METHOD: getNewsData ==> ", error);
                this.newsData = [];
            } finally {
                this.isLoadingInitialNews = false;
            }
        },

        async retrieveData() {
            this.homePageDocument = null;
            this.newsDocument = null;
            this.homePageData = null;
            this.newsData = null;

            await this.getHomePageData();
            await this.getNewsData();
            this.getBannerData();
            this.getCintilloData();
            this.getSidebarData();
        },

        async getAndSetCLCName(){
            if (this.clcSegmentValue) {
                // El mapeo clc_id -> clc_name es el mismo en todos los idiomas (nombres propios).
                // La variante es-es del documento suele traer el clc_list vacío/sin nombres, por lo
                // que en español el "your center" caía a "Otro". Si el idioma actual no resuelve el
                // nombre, se busca en la locale en-us (que siempre lo tiene).
                const nameFrom = (list) => {
                    if (!Array.isArray(list)) return '';
                    const match = list.find(clc => clc.clc_id === this.clcSegmentValue);
                    return (match && match.clc_name) || '';
                };
                let name = nameFrom(this.homePageData?.clc_list);
                if (!name && this.prismicLang !== 'en-us') {
                    try {
                        const enDoc = await this.$prismic.client.getSingle(CLC_HOME_PAGE_TYPE, { lang: 'en-us' });
                        name = nameFrom(enDoc?.data?.clc_list);
                    } catch (error) {
                        console.error("METHOD: getAndSetCLCName (en-us fallback) ==> ", error);
                    }
                }
                if (name) {
                    this.clcName = name;
                }
            }
            sessionStorage.setItem('clcSegmentValue', this.clcSegmentValue);
            sessionStorage.setItem('clcName', this.clcName);
        },
    },

    mounted() {
        this.sendPageView();
        // Guardar la ruta actual de la homepage en sessionStorage
        sessionStorage.setItem('lastHomepage', this.$route.path);
    },

    async created() {
        this.setLanguage();
        this.updateMeta();
        this.getCenterParam();
        try {
            await this.retrieveData();
            await this.getAndSetCLCName();
            // Precargar fondo + logo del rail para que no flashee el color de fondo al levantar el loader.
            await Promise.all([
                preloadImage(this.dynamicSidebar.bgImage),
                preloadImage(this.dynamicSidebar.logoSrc)
            ]);
        } finally {
            // Recién acá se sabe qué mostrar (y el fondo ya está descargado) -> se levanta el gate.
            this.pageReady = true;
        }
    }
}
</script>
